/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { IconButton, MenuItem, Paper, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { v4 } from "uuid";
import AutocompleteSelect from "../../../../Basis/Components/Autocomplete/AutocompleteSelect";
import DateTimeInput from "../../../../Basis/Components/DateTimeInput";
import DezimalInput from "../../../../Basis/Components/DezimalInput";
import GanzzahlInput from "../../../../Basis/Components/GanzzahlInput";
import LoadingIndicator from "../../../../Basis/Components/LoadingIndicator";
import MultipleSelect from "../../../../Basis/Components/MultipleSelect";
import SimpleSelect from "../../../../Basis/Components/SimpleSelect";
import TextInput from "../../../../Basis/Components/TextInput";
import { PlusIcon, TrashIcon } from "../../../../Basis/FontAwesome";
import AnyUtil, { nameof } from "../../../../Basis/Utils/AnyUtil";
import Filter, { FilterOperator } from "../../Commands/Filter";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nDomainDrivenDesign";
import FilterInfo from "../../Queries/FilterInfo";

export interface FilterTableProps<TFilter extends Filter> {
    filter: TFilter[];
    setFilter: React.Dispatch<React.SetStateAction<TFilter[]>>;
    findeFilterInfos: (suchbegriff: string) => Promise<FilterInfo[]>;
    commandName: string;
    pfadZuFilter: string[];
}

function ermittleKey(filter: Filter): string {
    if (!AnyUtil.isNullOrUndefined(filter.feld)) {
        return filter.feld;
    }

    if (!AnyUtil.isNullOrUndefined(filter.zusatzfeldDefinitionId)) {
        return filter.zusatzfeldDefinitionId;
    }

    throw new Error("Ungültiger Filter. Filter hat keinen Feldnamen und keine ZusatzfeldDefinitionId.", { cause: filter });
}

export default function FilterTable<TFilter extends Filter>(props: FilterTableProps<TFilter>) {
    const { filter, setFilter, findeFilterInfos } = props;
    const [filterInfos, setFilterInfos] = React.useState<{ [key: string]: FilterInfo }>({});
    const [key, setKey] = React.useState(v4());
    const { t } = useTranslation();

    const filterInfosKeys = Object.keys(filterInfos);
    const filterOhneFilterInfo = filter.filter(f => !filterInfosKeys.includes(ermittleKey(f)));
    React.useEffect(() => {
        filterOhneFilterInfo.forEach(f => {
            findeFilterInfos(ermittleKey(f)).then(filterInfos => {
                setFilterInfos(prev => filterInfos.reduce((acc, curr) => ({
                    ...acc,
                    [curr.feld]: curr,
                }), prev));
            });
        });
    }, [filterOhneFilterInfo.length]);

    function renderFilterWertInput(filter: Filter, idx: number): React.ReactNode {
        const commonProps = { ignoreTooltip: true, caption: "", typBezeichnung: props.commandName, required: true, autoFocus: true };
        switch (filter.wert.typ) {
            case "BOOLEAN":
                return (
                    <SimpleSelect
                        {...commonProps}
                        value={String(filter.wert.als.boolean ?? "")}
                        onChange={e => {
                            const booleanValue = e.target.value === "true";
                            setFilter(prev => prev.map((f, i) => {
                                if (i === idx) return { ...f, wert: { ...f.wert, als: { boolean: booleanValue } } };
                                return f;
                            }));
                        }}
                        feldBezeichnung={[
                            ...props.pfadZuFilter,
                            "filter",
                            String(idx),
                            nameof<Filter>("wert"),
                            nameof<Filter["wert"]>("als"),
                            nameof<Filter["wert"]["als"]>("boolean"),
                        ]}
                    >
                        <MenuItem style={{ display: "none" }} value=""></MenuItem>
                        <MenuItem value={String(true)}>{t(translationKeys.filterTable.ja)}</MenuItem>
                        <MenuItem value={String(false)}>{t(translationKeys.filterTable.nein)}</MenuItem>
                    </SimpleSelect>
                );
            case "ENUM": {
                const optionen = filterInfos[ermittleKey(filter)]?.optionen ?? [];
                const werte = optionen.map(option => String(option.als.enum));
                const namen = optionen.reduce(
                    (acc, curr) => {
                        const wert = String(curr.als.enum);
                        acc[wert] = curr.name;
                        return acc;
                    },
                    {} as { [wert: string]: string });
                return (
                    <SimpleSelect
                        {...commonProps}
                        value={String(filter.wert.als.enum ?? "")}
                        onChange={e => {
                            const enumValue = Number(e.target.value);
                            setFilter(prev => prev.map((f, i) => {
                                if (i === idx) return { ...f, wert: { ...f.wert, als: { enum: enumValue } } };
                                return f;
                            }));
                        }}
                        feldBezeichnung={[
                            ...props.pfadZuFilter,
                            "filter",
                            String(idx),
                            nameof<Filter>("wert"),
                            nameof<Filter["wert"]>("als"),
                            nameof<Filter["wert"]["als"]>("enum"),
                        ]}
                    >
                        {werte.map((wert, i) => (
                            <MenuItem key={i} value={wert}>{namen[wert]}</MenuItem>
                        ))}
                    </SimpleSelect>
                );
            }

            case "DECIMAL":
                return (
                    <DezimalInput
                        {...commonProps}
                        value={filter.wert.als.decimal ?? null}
                        onChange={decimal => {
                            setFilter(prev => prev.map((f, i) => {
                                if (i === idx) return { ...f, wert: { ...f.wert, als: { decimal: decimal ?? undefined } } };
                                return f;
                            }));
                        }}
                        feldBezeichnung={[
                            ...props.pfadZuFilter,
                            "filter",
                            String(idx),
                            nameof<Filter>("wert"),
                            nameof<Filter["wert"]>("als"),
                            nameof<Filter["wert"]["als"]>("decimal"),
                        ]}
                    />
                );
            case "INTEGER":
                return (
                    <GanzzahlInput
                        {...commonProps}
                        min={-1000000000}
                        max={1000000000}
                        value={filter.wert.als.integer ?? null}
                        onChange={integer => {
                            setFilter(prev => prev.map((f, i) => {
                                if (i === idx) return { ...f, wert: { ...f.wert, als: { integer: integer ?? undefined } } };
                                return f;
                            }));
                        }}
                        feldBezeichnung={[
                            ...props.pfadZuFilter,
                            "filter",
                            String(idx),
                            nameof<Filter>("wert"),
                            nameof<Filter["wert"]>("als"),
                            nameof<Filter["wert"]["als"]>("integer"),
                        ]}
                    />
                );
            case "GUID":
                return (
                    <TextInput
                        {...commonProps}
                        value={filter.wert.als.guid ?? ""}
                        onChange={e => {
                            const value = e.target.value;
                            setFilter(prev => prev.map((f, i) => {
                                if (i === idx) return { ...f, wert: { ...f.wert, als: { guid: value } } };
                                return f;
                            }));
                        }}
                        feldBezeichnung={[
                            ...props.pfadZuFilter,
                            "filter",
                            String(idx),
                            nameof<Filter>("wert"),
                            nameof<Filter["wert"]>("als"),
                            nameof<Filter["wert"]["als"]>("guid"),
                        ]}
                    />
                );
            case "STRING":
                return (
                    <TextInput
                        {...commonProps}
                        value={filter.wert.als.string ?? ""}
                        onChange={e => {
                            const value = e.target.value;
                            setFilter(prev => prev.map((f, i) => {
                                if (i === idx) return { ...f, wert: { ...f.wert, als: { string: value } } };
                                return f;
                            }));
                        }}
                        feldBezeichnung={[
                            ...props.pfadZuFilter,
                            "filter",
                            String(idx),
                            nameof<Filter>("wert"),
                            nameof<Filter["wert"]>("als"),
                            nameof<Filter["wert"]["als"]>("string"),
                        ]}
                    />
                );
            case "DATETIME":
                return (
                    <DateTimeInput
                        {...commonProps}
                        ignoreTooltip
                        autoFocus
                        value={filter.wert.als.dateTime ?? ""}
                        onChange={value => {
                            setFilter(prev => prev.map((f, i) => {
                                if (i === idx) return { ...f, wert: { ...f.wert, als: { dateTime: value } } };
                                return f;
                            }));
                        }}
                        caption=""
                        feldBezeichnung={[
                            ...props.pfadZuFilter,
                            "filter",
                            String(idx),
                            nameof<Filter>("wert"),
                            nameof<Filter["wert"]>("als"),
                            nameof<Filter["wert"]["als"]>("dateTime"),
                        ]}
                    />
                );
            case "STRINGARRAY": {
                const optionen = filterInfos[ermittleKey(filter)]?.optionen ?? [];
                const werte = optionen.map(option => option.als.string ?? "");
                const namen = optionen.reduce(
                    (acc, curr) => {
                        const wert = curr.als.string ?? "";
                        acc[wert] = curr.name;
                        return acc;
                    },
                    {} as { [wert: string]: string });

                const handleOptionDelete = (wert: string) => {
                    setFilter(prev => prev.map((f, i) => {
                        if (i === idx) {
                            return {
                                ...f,
                                wert: {
                                    ...f.wert,
                                    als: {
                                        stringArray: (f.wert.als.stringArray ?? []).filter(w => w !== wert)
                                    },
                                },
                            };
                        }

                        return f;
                    }));
                };

                return (
                    <MultipleSelect
                        {...commonProps}
                        optionen={werte}
                        render={wert => namen[wert]}
                        value={filter.wert.als.stringArray ?? []}
                        onChange={value => {
                            setFilter(prev => prev.map((f, i) => {
                                if (i === idx) return { ...f, wert: { ...f.wert, als: { stringArray: value } } };
                                return f;
                            }));
                        }}
                        onDelete={handleOptionDelete}
                        feldBezeichnung={[
                            ...props.pfadZuFilter,
                            "filter",
                            String(idx),
                            nameof<Filter>("wert"),
                            nameof<Filter["wert"]>("als"),
                            nameof<Filter["wert"]["als"]>("stringArray"),
                        ]}
                    />
                );
            }

            default:
                return null;
        }
    }

    const handleFeldChange = (idx: number) => (filterInfo: FilterInfo | null) => {
        if (!filterInfo) {
            return;
        }

        setFilterInfos(prev => ({
            ...prev,
            [filterInfo.feld]: filterInfo,
        }));
        setFilter(prev => prev.map((filter, i) => {
            if (i === idx) {
                return {
                    ...filter,
                    feld: filterInfo.feld,
                    operator: filterInfo.operatoren[0],
                    wert: { typ: filterInfo.typ, als: {} },
                }
            }

            return filter;
        }));
    };

    const handleOperatorChange = (idx: number) => (e: SelectChangeEvent<unknown>) => {
        const operator = e.target.value as FilterOperator;
        setFilter(prev => prev.map((filter, i) => {
            if (i === idx) return { ...filter, operator };
            return filter;
        }));
    };

    const handleLoeschenBtnClick = (idx: number) => () =>
        setFilter(prev => prev.filter((_, i) => i !== idx));

    function handleFilterHinzufuegen(filterInfo: FilterInfo | null) {
        if (!filterInfo) return;
        setFilterInfos(prev => ({ ...prev, [filterInfo.feld]: filterInfo }));
        setFilter(prev => {
            const filter = {
                feld: filterInfo.feld,
                operator: filterInfo.operatoren[0],
                wert: { typ: filterInfo.typ, als: {} },
                zusatzfeldDefinitionId: filterInfo.zusatzfeldDefinitionId,
            } as TFilter;
            return [...prev, filter];
        });

        // Rerendern erzwingen
        setKey(v4());
    }

    return (
        <TableContainer component={Paper} variant="outlined">
            <Table stickyHeader>
                <colgroup>
                    <col width="25%" />
                    <col width="25%" />
                    <col width="calc(50% - 72px)" />
                    <col width="72px" />
                </colgroup>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            {t(translationKeys.filterTable.spalte)}
                        </TableCell>
                        <TableCell>
                            {t(translationKeys.filterTable.operator)}
                        </TableCell>
                        <TableCell>
                            {t(translationKeys.filterTable.wert)}
                        </TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filterOhneFilterInfo.length > 0 ? (
                        <TableRow>
                            <TableCell colSpan={4}>
                                <LoadingIndicator loading />
                            </TableCell>
                        </TableRow>
                    ) : (filter.map((filter, idx) => {
                        return (
                            <TableRow key={idx}>
                                <TableCell>
                                    <AutocompleteSelect<FilterInfo>
                                        ignoreTooltip
                                        placeholder={t(translationKeys.filterTable.filterHinzufuegen)}
                                        value={filterInfos[ermittleKey(filter)]}
                                        onChange={handleFeldChange(idx)}
                                        getOptionLabel={filterInfo => filterInfo.feld}
                                        getOptionSelected={(option, value) => option === value}
                                        executeQuery={findeFilterInfos}
                                        caption=""
                                        typBezeichnung={props.commandName}
                                        feldBezeichnung={["filter", String(idx), nameof<Filter>("feld")]} />
                                </TableCell>
                                <TableCell>
                                    <SimpleSelect
                                        ignoreTooltip
                                        value={filter.operator}
                                        onChange={handleOperatorChange(idx)}
                                        caption=""
                                        typBezeichnung={props.commandName}
                                        feldBezeichnung={["filter", String(idx), nameof<Filter>("operator")]}
                                    >
                                        {(filterInfos[ermittleKey(filter)]?.operatoren ?? []).map(operator => (
                                            <MenuItem key={operator} value={operator}>
                                                {t(translationKeys.filterTable[operator])}
                                            </MenuItem>
                                        ))}
                                    </SimpleSelect>
                                </TableCell>
                                <TableCell>
                                    {renderFilterWertInput(filter, idx)}
                                </TableCell>
                                <TableCell>
                                    <IconButton onClick={handleLoeschenBtnClick(idx)}>
                                        <TrashIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        );
                    }))}
                    <TableRow>
                        <TableCell colSpan={4}>
                            <AutocompleteSelect<FilterInfo | null>
                                key={key}
                                ignoreTooltip
                                InputProps={{ startAdornment: <PlusIcon color="action" /> }}
                                placeholder={t(translationKeys.filterTable.filterHinzufuegen)}
                                value={null}
                                onChange={handleFilterHinzufuegen}
                                getOptionLabel={filterInfo => filterInfo?.feld ?? ""}
                                getOptionSelected={(option, value) => option === value}
                                executeQuery={findeFilterInfos}
                                caption=""
                                typBezeichnung=""
                                feldBezeichnung=""
                            />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}
